import ejs from 'ejs';

/* ------------------------------------------------------------------ *\
JSON Static blocks - generic code to easily replace
loops by json static.
Example HTML:

<div class="js-json-static" data-url="#$json|key-for-json$#">
    <form class="js-json-static-form">
        <input type="hidden" name="limit" value="5">
        <input type="text" name="is_featured" value="1" class="js-json-static-change-listener">
    </form>

    <div class="js-json-static-results"></div>

    <script type="text/template">
        <% if (items && items.length) { %>
            <% items.forEach(function(item){ %>
                <div><%= item.id %> - <%= item.name %></div>
            <% }) %>
        <% } %>
    </script>
</div>
\* ------------------------------------------------------------ */

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
function debounce(func, wait, immediate) {
    var timeout;
    return function () {
        var context = this,
            args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
};

class jsonStatic {
    constructor(container, callbackFunction) {
        this.debug = true; // activate debug during development, then you can put it to false

        this.errors = [];

        this.container = container;
        this.form = this.container.querySelector('.js-json-static-form');

        this.templates = this.container.querySelectorAll('script[type="text/template"]');
        this.url = this.container.getAttribute('data-url');
        this.data = window[this.container.getAttribute('data-variable')];
        this.callbackFunction = callbackFunction;

        this.filters = {};
        
        if (!this.url && !this.data) {
            this.errors.push('json source missing');
        }

        if (this.templates.length < 1) {
            this.errors.push('script[type="text/template"] tag missing');
        }

        if (this.errors.length) {
            this.errors.forEach(text => console.error(text));
            return;
        }

        if (this.container.getAttribute('autosearch') != 'false') { // disable autosearch to display static html content on page load
            this.debouncedApiRequest();
        }

        if (this.form) {
            this.form.addEventListener('submit', event => {
                event.preventDefault();

                this.debouncedApiRequest();
            });
        }
    }

    // Do not call directly apiRequest,
    // always use debouncedApiRequest
    async apiRequest () {
        this.getFilters();

        this.container.classList.add('loading');

        const options = {
            debug: this.debug,
            filters: this.filters,
        }

        if (this.url) {
            options.url = this.url;
        } else {
            options.data = this.data;
        }

        window.GalaxyHelpers.staticSearch(options).then(data => {
            const { results, count } = data;

            this.container.classList.remove('loading');

            this.displayContent(results);

        });
    }

    // avoids calling the API many times if
    // someone spams requests (for example many click on submit)
    // or if there is an auto-submit when fields changes
    debouncedApiRequest = debounce(this.apiRequest, 300)

    getFilters () {
        if (this.form) {
            for(var pair of new FormData(this.form).entries()) {
                // Handle checkboxes case
                if (this.filters[pair[0]]) {
                    this.filters[pair[0]] = this.filters[pair[0]] + ',' + pair[1]
                } else {
                    this.filters[pair[0]] = pair[1]
                }
            }
        }
    }

    displayContent (results) {
        this.container.querySelectorAll('.js-json-static-results').forEach(resultWrapper => {
            const identifier = resultWrapper.getAttribute('data-template');

            /* Find the matching script[type="text/template"] tags */
            const template = Array.from(this.templates).find(template => template.getAttribute('id') == identifier)

            if (!template) {
                console.error(`template with id '${identifier}' is missing`);
                return;
            }

            try {
                const source = ejs.compile(template.innerHTML);

                const compiledHTML = source({
                    error: false,
                    items: results
                });

                if (this.filters.page && this.filters.page > 1) {
                    const content = document.createElement('DIV');
                    content.innerHTML = compiledHTML;
                    resultWrapper.appendChild(content);
                } else {
                    resultWrapper.innerHTML = compiledHTML;
                }

                if (typeof this.callbackFunction === 'function') {
                    this.callbackFunction.call(undefined, resultWrapper, results);
                }


            } catch (error) {
                console.error(error);
            }
        });
    }
}

/**
 * @callback callbackFunction
 * @param  {HTMLElement} el - Current element
 * @param  {object[]} data - Loaded data for this element
 */

/**
 * JSON Static blocks - generic code to easily replace
 * loops by json static for the element with "js-json-static" class
 *
 * The callback function will be called for each
 * .js-json-static replaced elements
 *
 * @param {object} [config={}] configuration object
 * @param {callbackFunction} config.callback callback function
 */
export default (config = {}) => {
    const callbackFunction = config.callback || false;

    document.querySelectorAll('.js-json-static').forEach(el => {
        new jsonStatic(el, callbackFunction);
    });
}
